import ReactGA from 'react-ga4'

/**
 * Google Analytics Configuration for Single Page Application
 * Enhanced tracking for behavioral insights and user engagement
 */

// Session tracking variables
let sessionStartTime = null
let currentPageStartTime = null
let scrollDepthTracked = new Set()
let engagementTimer = null

// Initialize Google Analytics with enhanced configuration
export const initGA = () => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
  
  if (measurementId) {
    ReactGA.initialize(measurementId, {
      gaOptions: {
        anonymizeIp: true,
        siteSpeedSampleRate: 100, // Track all page load times
        cookieFlags: 'SameSite=None;Secure',
      },
    })
    
    // Set session start time
    sessionStartTime = Date.now()
    
    // Track initial page load
    trackPageView(window.location.pathname)
    
    // Initialize scroll depth tracking
    initScrollDepthTracking()
    
    // Initialize engagement tracking
    initEngagementTracking()
    
    console.log('Google Analytics initialized with enhanced SPA tracking')
  } else {
    console.warn('Google Analytics Measurement ID not found. Set VITE_GA_MEASUREMENT_ID in your .env file')
  }
}

/**
 * Track virtual page views for SPA navigation
 * This is crucial for single-page apps to track navigation
 */
export const trackPageView = (path, title = '') => {
  // Calculate time on previous page
  if (currentPageStartTime) {
    const timeOnPage = Math.round((Date.now() - currentPageStartTime) / 1000)
    ReactGA.event({
      category: 'Engagement',
      action: 'Time on Page',
      label: window.location.pathname,
      value: timeOnPage,
    })
  }
  
  // Reset page start time
  currentPageStartTime = Date.now()
  
  // Reset scroll depth tracking for new page
  scrollDepthTracked.clear()
  
  // Send pageview
  ReactGA.send({ 
    hitType: 'pageview', 
    page: path,
    title: title || document.title
  })
  
  // Track page load time
  if (window.performance && window.performance.timing) {
    const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart
    if (loadTime > 0) {
      ReactGA.event({
        category: 'Performance',
        action: 'Page Load Time',
        label: path,
        value: loadTime,
      })
    }
  }
}

/**
 * Track custom events with enhanced parameters
 */
export const trackEvent = (category, action, label = '', value = 0, customParams = {}) => {
  ReactGA.event({
    category,
    action,
    label,
    value,
    ...customParams,
  })
}

/**
 * Initialize scroll depth tracking
 * Tracks how far users scroll on the page (25%, 50%, 75%, 100%)
 */
const initScrollDepthTracking = () => {
  let ticking = false
  
  const trackScrollDepth = () => {
    const scrollPercentage = Math.round(
      ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
    )
    
    const milestones = [25, 50, 75, 100]
    milestones.forEach(milestone => {
      if (scrollPercentage >= milestone && !scrollDepthTracked.has(milestone)) {
        scrollDepthTracked.add(milestone)
        ReactGA.event({
          category: 'Engagement',
          action: 'Scroll Depth',
          label: `${milestone}%`,
          value: milestone,
        })
      }
    })
    
    ticking = false
  }
  
  window.addEventListener('scroll', () => {
    if (!ticking) {
      window.requestAnimationFrame(trackScrollDepth)
      ticking = true
    }
  })
}

/**
 * Initialize engagement tracking
 * Tracks active engagement time (user is actively interacting)
 */
const initEngagementTracking = () => {
  let engagementTime = 0
  let isEngaged = true
  
  // Track engagement every 10 seconds
  engagementTimer = setInterval(() => {
    if (isEngaged) {
      engagementTime += 10
      
      // Send engagement event every minute
      if (engagementTime % 60 === 0) {
        ReactGA.event({
          category: 'Engagement',
          action: 'Active Time',
          label: window.location.pathname,
          value: engagementTime,
        })
      }
    }
  }, 10000)
  
  // Track user activity
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
  activityEvents.forEach(event => {
    document.addEventListener(event, () => {
      isEngaged = true
    })
  })
  
  // Track inactivity
  document.addEventListener('visibilitychange', () => {
    isEngaged = !document.hidden
  })
}

/**
 * Track user sign-up events
 */
export const trackSignUp = (method) => {
  ReactGA.event({
    category: 'User',
    action: 'Sign Up',
    label: method, // 'email' or 'google'
  })
}

/**
 * Track user sign-in events
 */
export const trackSignIn = (method) => {
  ReactGA.event({
    category: 'User',
    action: 'Sign In',
    label: method, // 'email' or 'google'
  })
}

/**
 * Track tournament registration events
 */
export const trackTournamentRegistration = (tournamentId, categoryId, numberOfPlayers) => {
  ReactGA.event({
    category: 'Tournament',
    action: 'Registration',
    label: `${tournamentId} - ${categoryId}`,
    value: numberOfPlayers,
  })
}

/**
 * Track registration form steps
 */
export const trackRegistrationStep = (step, totalSteps) => {
  ReactGA.event({
    category: 'Registration Flow',
    action: 'Step Progress',
    label: `Step ${step} of ${totalSteps}`,
    value: step,
  })
}

/**
 * Track payment proof upload
 */
export const trackPaymentProofUpload = (tournamentId) => {
  ReactGA.event({
    category: 'Tournament',
    action: 'Payment Proof Upload',
    label: tournamentId,
  })
}

/**
 * Track registration errors
 */
export const trackRegistrationError = (errorMessage) => {
  ReactGA.event({
    category: 'Registration Flow',
    action: 'Error',
    label: errorMessage,
  })
}

/**
 * Track profile updates
 */
export const trackProfileUpdate = () => {
  ReactGA.event({
    category: 'User',
    action: 'Profile Update',
  })
}

// ============================================================================
// ADVANCED BEHAVIORAL TRACKING FOR TOURNAMENT VIEWER SPA
// ============================================================================

/**
 * Set user properties for segmentation
 * Helps understand user demographics and behavior patterns
 */
export const setUserProperties = (properties) => {
  ReactGA.set(properties)
}

/**
 * Track tournament view
 * Understand which tournaments attract the most attention
 */
export const trackTournamentView = (tournamentId, tournamentName, status) => {
  ReactGA.event({
    category: 'Tournament',
    action: 'View',
    label: tournamentName,
    tournament_id: tournamentId,
    tournament_status: status,
  })
}

/**
 * Track category browsing
 * Understand which categories users are interested in
 */
export const trackCategoryView = (categoryId, categoryName, tournamentId) => {
  ReactGA.event({
    category: 'Tournament',
    action: 'Category View',
    label: categoryName,
    category_id: categoryId,
    tournament_id: tournamentId,
  })
}

/**
 * Track category expansion/collapse
 * Understand user interaction patterns with category cards
 */
export const trackCategoryInteraction = (action, categoryName) => {
  ReactGA.event({
    category: 'UI Interaction',
    action: `Category ${action}`, // 'Expand' or 'Collapse'
    label: categoryName,
  })
}

/**
 * Track registration modal open
 * Understand registration intent vs actual registrations (conversion funnel)
 */
export const trackRegistrationModalOpen = (tournamentId, categoryId, categoryName) => {
  ReactGA.event({
    category: 'Registration Flow',
    action: 'Modal Opened',
    label: categoryName,
    tournament_id: tournamentId,
    category_id: categoryId,
  })
}

/**
 * Track registration modal abandonment
 * Understand where users drop off in the registration process
 */
export const trackRegistrationAbandonment = (step, totalSteps, reason = '') => {
  ReactGA.event({
    category: 'Registration Flow',
    action: 'Abandoned',
    label: `Step ${step} of ${totalSteps}`,
    value: step,
    abandonment_reason: reason,
  })
}

/**
 * Track registration completion time
 * Understand how long it takes users to complete registration
 */
export const trackRegistrationDuration = (durationSeconds, numberOfPlayers) => {
  ReactGA.event({
    category: 'Registration Flow',
    action: 'Completion Time',
    label: `${numberOfPlayers} player(s)`,
    value: durationSeconds,
  })
}

/**
 * Track theme toggle
 * Understand user preference for dark/light mode
 */
export const trackThemeToggle = (theme) => {
  ReactGA.event({
    category: 'UI Interaction',
    action: 'Theme Toggle',
    label: theme, // 'dark' or 'light'
  })
}

/**
 * Track authentication modal interactions
 * Understand user behavior in auth flow
 */
export const trackAuthModalAction = (action, mode) => {
  ReactGA.event({
    category: 'Authentication',
    action: action, // 'Opened', 'Closed', 'Switched Mode'
    label: mode, // 'signin' or 'signup'
  })
}

/**
 * Track search/filter usage (if implemented)
 * Understand how users find tournaments/categories
 */
export const trackSearch = (searchTerm, resultsCount) => {
  ReactGA.event({
    category: 'Search',
    action: 'Query',
    label: searchTerm,
    value: resultsCount,
  })
}

/**
 * Track button clicks and CTA interactions
 * Understand which CTAs are most effective
 */
export const trackCTAClick = (ctaName, location) => {
  ReactGA.event({
    category: 'CTA',
    action: 'Click',
    label: ctaName,
    cta_location: location,
  })
}

/**
 * Track error occurrences
 * Understand technical issues users encounter
 */
export const trackError = (errorType, errorMessage, context = '') => {
  ReactGA.event({
    category: 'Error',
    action: errorType,
    label: errorMessage,
    error_context: context,
    non_interaction: true, // Don't affect bounce rate
  })
}

/**
 * Track session quality score
 * Custom metric to understand overall session quality
 */
export const trackSessionQuality = () => {
  const sessionDuration = sessionStartTime ? Math.round((Date.now() - sessionStartTime) / 1000) : 0
  const quality = sessionDuration > 300 ? 'High' : sessionDuration > 60 ? 'Medium' : 'Low'
  
  ReactGA.event({
    category: 'Session',
    action: 'Quality Score',
    label: quality,
    value: sessionDuration,
  })
}

/**
 * Track outbound links
 * Understand where users go when they leave
 */
export const trackOutboundLink = (url, linkText) => {
  ReactGA.event({
    category: 'Outbound Link',
    action: 'Click',
    label: url,
    link_text: linkText,
  })
}

/**
 * Track file downloads (e.g., tournament data export)
 */
export const trackFileDownload = (fileName, fileType) => {
  ReactGA.event({
    category: 'Download',
    action: fileType,
    label: fileName,
  })
}

/**
 * Enhanced ecommerce-style tracking for registrations
 * Provides funnel analysis and conversion insights
 */
export const trackRegistrationFunnel = {
  // Step 1: View category (product impression)
  viewCategory: (categoryId, categoryName, tournamentName, price = 0) => {
    ReactGA.event({
      category: 'Ecommerce',
      action: 'Product Impression',
      label: categoryName,
      ecommerce: {
        items: [{
          item_id: categoryId,
          item_name: categoryName,
          item_category: 'Tournament Registration',
          item_brand: tournamentName,
          price: price,
        }]
      }
    })
  },
  
  // Step 2: Click register button (add to cart)
  initiateRegistration: (categoryId, categoryName, tournamentName, price = 0) => {
    ReactGA.event({
      category: 'Ecommerce',
      action: 'Add to Cart',
      label: categoryName,
      ecommerce: {
        items: [{
          item_id: categoryId,
          item_name: categoryName,
          item_category: 'Tournament Registration',
          item_brand: tournamentName,
          price: price,
          quantity: 1,
        }]
      }
    })
  },
  
  // Step 3: Begin checkout (start registration form)
  beginCheckout: (categoryId, categoryName, tournamentName, price = 0) => {
    ReactGA.event({
      category: 'Ecommerce',
      action: 'Begin Checkout',
      label: categoryName,
      ecommerce: {
        items: [{
          item_id: categoryId,
          item_name: categoryName,
          item_category: 'Tournament Registration',
          item_brand: tournamentName,
          price: price,
          quantity: 1,
        }]
      }
    })
  },
  
  // Step 4: Complete purchase (successful registration)
  completePurchase: (transactionId, categoryId, categoryName, tournamentName, price = 0) => {
    ReactGA.event({
      category: 'Ecommerce',
      action: 'Purchase',
      label: categoryName,
      ecommerce: {
        transaction_id: transactionId,
        value: price,
        currency: 'PHP',
        items: [{
          item_id: categoryId,
          item_name: categoryName,
          item_category: 'Tournament Registration',
          item_brand: tournamentName,
          price: price,
          quantity: 1,
        }]
      }
    })
  }
}

/**
 * Track user journey milestones
 * Understand the path users take through the app
 */
export const trackUserJourney = (milestone, metadata = {}) => {
  ReactGA.event({
    category: 'User Journey',
    action: milestone,
    ...metadata,
  })
}

// ============================================================================
// DETAILED ACTIVITY TRACKING - WHO, WHAT, WHY
// ============================================================================

/**
 * Track main tab switches (info, brackets, schedule, results)
 */
export const trackTabSwitch = (tabName, tournamentId = '') => {
  ReactGA.event({
    category: 'Navigation',
    action: 'Tab Switch',
    label: tabName,
    tournament_id: tournamentId,
  })
}

/**
 * Track schedule interactions
 * Actions: Viewed, Day Selected, Category Filtered, Player Search, Filter Toggle
 */
export const trackScheduleActivity = (action, details = {}) => {
  ReactGA.event({
    category: 'Schedule',
    action,
    label: details.label || '',
    value: details.value || 0,
    ...details,
  })
}

/**
 * Track bracket interactions
 * Actions: Viewed, Day Selected, Category Selected
 */
export const trackBracketActivity = (action, details = {}) => {
  ReactGA.event({
    category: 'Brackets',
    action,
    label: details.label || '',
    value: details.value || 0,
    ...details,
  })
}

/**
 * Track live scores interactions
 * Actions: Viewed, Category Selected, Day Selected
 */
export const trackLiveScoresActivity = (action, details = {}) => {
  ReactGA.event({
    category: 'Live Scores',
    action,
    label: details.label || '',
    value: details.value || 0,
    ...details,
  })
}

/**
 * Track category detail page interactions
 * Actions: Viewed, Tab Switch, Team Selected, Share Card, Manual Refresh
 */
export const trackCategoryDetailActivity = (action, details = {}) => {
  ReactGA.event({
    category: 'Category Detail',
    action,
    label: details.label || '',
    value: details.value || 0,
    ...details,
  })
}

/**
 * Track email verification interactions
 * Actions: Resend Success, Resend Error, Banner Dismissed, Restricted Access
 */
export const trackEmailVerification = (action, details = {}) => {
  ReactGA.event({
    category: 'Email Verification',
    action,
    label: details.label || '',
    ...details,
  })
}

/**
 * Track user sign-out
 */
export const trackSignOut = () => {
  ReactGA.event({
    category: 'User',
    action: 'Sign Out',
  })
}

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackSignUp,
  trackSignIn,
  trackTournamentRegistration,
  trackRegistrationStep,
  trackPaymentProofUpload,
  trackRegistrationError,
  trackProfileUpdate,
  setUserProperties,
  trackTournamentView,
  trackCategoryView,
  trackCategoryInteraction,
  trackRegistrationModalOpen,
  trackRegistrationAbandonment,
  trackRegistrationDuration,
  trackThemeToggle,
  trackAuthModalAction,
  trackSearch,
  trackCTAClick,
  trackError,
  trackSessionQuality,
  trackOutboundLink,
  trackFileDownload,
  trackRegistrationFunnel,
  trackUserJourney,
  trackTabSwitch,
  trackScheduleActivity,
  trackBracketActivity,
  trackLiveScoresActivity,
  trackCategoryDetailActivity,
  trackEmailVerification,
  trackSignOut,
}
